import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { TmsWebBaseEditCreateCopyService } from '../base';
import {
  AddressLabel,
  CreatedSalesOrderFromContracts,
  CreditPassed,
  ProformaPaymentAnalysis,
  SalesOrderAnalysis,
  SalesOrderConfirmedAudit,
  SalesOrderConfirmResult,
  SalesOrderEmailContacts,
  SalesOrderHeader,
  SalesOrderHeaderDetails,
  SalesOrderOnStopStatus,
} from '../../models';
import { OnStopOverrideStages } from '../../constants';

@Injectable({
  providedIn: 'root',
})
export class SalesOrderService extends TmsWebBaseEditCreateCopyService<SalesOrderHeader, SalesOrderHeaderDetails> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('SalesOrder', $localize`Sales Order Header`);
  }

  getSalesOrderHeader(id: string): Observable<SalesOrderHeader> {
    return this.httpService.get<SalesOrderHeader>(`${this.apiUrl}/SalesOrder?id=${id}`);
  }

  getSalesOrderEmailContacts(id: string): Observable<SalesOrderEmailContacts> {
    return this.httpService.get<SalesOrderEmailContacts>(`${this.apiUrl}/SalesOrderEmailContacts?id=${id}`);
  }

  checkAutoCreatePO(id: string): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckAutoCreatePO?id=${id}`);
  }

  checkUseTaxInclusivePricing(id: string): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckUseTaxInclusivePricing?id=${id}`);
  }

  getDeliveryAddress(id: string, salesDespatchNoteId: string): Observable<AddressLabel> {
    return this.httpService.get<AddressLabel>(
      `${this.apiUrl}/GetDeliveryAddress?id=${id}&salesDespatchNoteId=${salesDespatchNoteId}`,
    );
  }

  getConfirmedAudit(id: string): Observable<SalesOrderConfirmedAudit[]> {
    return this.httpService.get<SalesOrderConfirmedAudit[]>(`${this.apiUrl}/GetConfirmedAudit?id=${id}`);
  }

  updateCreditPassed(creditPassed: CreditPassed): Observable<CreditPassed> {
    return this.httpService.post<CreditPassed>(`${this.apiUrl}/CreditPassed`, creditPassed);
  }

  // acceptOnStop / acceptCreditCheckFailed are sent true only after the user has said Yes to M4 /
  // M10, re-sending the same confirm; the 200 body names a credit check that could not run (M12).
  updateConfirmStatus(
    id: string,
    manual: boolean,
    acceptOnStop = false,
    acceptCreditCheckFailed = false,
  ): Observable<SalesOrderConfirmResult> {
    return this.httpService.post<SalesOrderConfirmResult>(`${this.apiUrl}/UpdateConfirmStatus`, {
      id,
      manual,
      acceptOnStop,
      acceptCreditCheckFailed,
    });
  }

  getOnStopStatus(id: string): Observable<SalesOrderOnStopStatus> {
    return this.httpService.get<SalesOrderOnStopStatus>(`${this.apiUrl}/OnStopStatus/${id}`);
  }

  // Records a Picking or Despatch override for an on-stop customer's order. The API checks the
  // user's On Stop Override permission and answers with the order's on-stop state afterwards.
  overrideOnStop(salesOrderHeaderId: string, stage: OnStopOverrideStages): Observable<SalesOrderOnStopStatus> {
    return this.httpService.post<SalesOrderOnStopStatus>(`${this.apiUrl}/OverrideOnStop`, {
      salesOrderHeaderId,
      stage,
    });
  }

  updateCurrency(salesOrderHeaderId: string, currencyId: string): Observable<void> {
    return this.httpService.post<void>(`${this.apiUrl}/UpdateCurrency`, { salesOrderHeaderId, currencyId });
  }

  createSalesOrderFromContracts(ids: string[], balances: number[]): Observable<string[]> {
    return this.httpService.post<string[]>(`${this.apiUrl}/CreateSalesOrderFromContracts`, { ids, balances });
  }

  createSalesOrderCustomerPortal(id: string): Observable<SalesOrderHeader> {
    return this.httpService.post<SalesOrderHeader>(`${this.apiUrl}/CreateSalesOrderFromCustomerPortal`, { id });
  }

  getSalesOrderAnalysis(fromDate: Date, toDate: Date, useDateEntered: boolean): Observable<SalesOrderAnalysis[]> {
    return this.httpService.get<SalesOrderAnalysis[]>(
      `${this.apiUrl}/SalesOrderAnalysis?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}&useDateEntered=${useDateEntered}`,
    );
  }

  getProformaOrderAnalysis(fromDate: Date, toDate: Date): Observable<ProformaPaymentAnalysis[]> {
    return this.httpService.get<ProformaPaymentAnalysis[]>(
      `${this.apiUrl}/ProformaOrderAnalysis?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`,
    );
  }

  getCreatedSalesOrdersFromContracts(ids: string[]): Observable<CreatedSalesOrderFromContracts[]> {
    return this.httpService.post<CreatedSalesOrderFromContracts[]>(`${this.apiUrl}/CreatedSalesOrdersFromContracts`, {
      ids,
    });
  }
}
